import type { DrawContext } from './draw';
import { strand } from './draw';
import type { View } from './project';
import { toScreen } from './project';
import type { EdgeFan } from './fan';

/*
 * THE WEB OVER THE NAMES THAT ARE ALWAYS HERE — the resident nodes' ties.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` lines 1513–1562.
 *
 * Distinct from `sky-edges.ts`, which draws the ties of names STREAMED in from tiles. These are the
 * roster's own, held in memory from the first frame, and they get a richer treatment: a tie here
 * knows whether the reader has walked both of its ends, one, or neither, and it says so.
 *
 * THREE CLASSES, AND THEY ARE THE POINT OF THE LAYER:
 *
 *   walked   both ends opened — the reader has been down this road
 *   open     one end opened — the road out of somewhere they know
 *   rumour   neither, or the tie is soft — a connection they have been told about
 *
 * Their comment says it plainly: *"the same strand renderer the selected node uses, just fainter
 * for the ambient web"*. One renderer, one alpha scale, so a reader learns the vocabulary once.
 *
 * AND IT FADES TOWARD WHAT IS OPEN. `NEAR` brightens ties near the focused name and `DIM` darkens
 * every class when anything is open at all, so opening a figure does not add a highlight — it takes
 * the light out of everything else. The reader's attention is directed by subtraction.
 *
 * The gates, in the order they are cheapest: off screen, too long in the WORLD, too short on the
 * SCREEN, too weak, and finally the per-name fan. The fan is checked before the geometry and
 * charged only after — the same discipline as `sky-edges.ts`.
 *
 * (Their loop carries an `onSel` flag guarding four of those gates, and it is dead: the same
 * condition has already `continue`d two lines above, so `onSel` is false every time it is read.
 * Not carried over. It reads as the trace of an earlier design where the open name's ties were
 * drawn here rather than by the selected fan.)
 */

/** One resident name, as this layer needs it. */
export interface WebNode {
  x: number;
  y: number;
  /** Its radius in world units — what sets the gap a strand leaves at each end. */
  r: number;
  /** Its weight. The importance gate is measured against this. */
  w: number;
  /** Which scriptures name it, one character each. */
  cs: string;
  /** What kind of thing it is, for the reader's own filter. */
  kind: string;
}

/** One tie: two node indices. Theirs is a tuple and stays one — 18,000 of them. */
export type WebEdge = readonly [number, number];

/** What the reader has switched off: `k_<kind>` and `c_<corpus>` keys, exactly as theirs. */
export type OffSwitches = Readonly<Record<string, boolean | undefined>>;

/**
 * THE WORLD DIMENSIONS THIS LAYER MEASURES AGAINST. All arguments, none with a default.
 */
export interface WebWorld {
  /**
   * The longest tie drawn when nothing is open, in WORLD units.
   *
   * Theirs is 950 against a rim of 12,320 — under a tenth of the field. World units rather than
   * screen, so a zoom never changes which ties exist; the same reasoning as `sky-edges.ts`, where
   * their own comment says so.
   */
  ambientMax: number;
  /**
   * The weight both ends must reach for an ambient tie to be drawn at all.
   *
   * Theirs is 4, measured against a roster whose synthetic filler tops out at 3 — so on their build
   * this gate is largely what separates the real names from the filler. Ours means something else
   * entirely, which is why it cannot be copied.
   */
  minWeight: number;
  /** The world's vertical scale, so a tie's length is measured in the world. */
  squash: number;
}

/**
 * THE ZOOMS THE LAYER CHANGES AT — arguments, because a zoom is pixels per world unit.
 *
 * A world ten times the size is read at a tenth the zoom. Their 0.14, 0.55 and 0.6 are the fourth,
 * fifth and sixth absolute zooms this port has had to lift out; the wash fade was the first, and
 * getting it wrong is what kept the first port's overdraw on through the whole opening flight.
 */
export interface WebZooms {
  /** Below this the web is not drawn at all — the names are dots and the ties would be a haze. */
  from: number;
  /** Where the web starts gathering toward what is open, and over how much zoom. */
  poolFrom: number;
  poolSpan: number;
}

/** How many ties one name may carry before the rest are dropped. A budget, so mechanism. */
const FAN_CAP = 4;

/** How far the curve bows away from the straight line between two names. */
const BEND = 0.13;

/** The three classes' head alphas — walked, open, rumour — as in the selected fan. */
const HEAD = [0.46, 0.4, 0.26];

/** How much brighter a tie gets for being near what the reader has open, by distance band. */
const NEAR = [0.3, 0.66, 1];

/** The ambient web's own ceiling, which rises as the reader comes in. */
const AMBIENT_FLOOR = 0.2;
const AMBIENT_RANGE = 0.26;

/** How much the light is taken OUT of each class when something is open. */
const DIMMED = [0.5, 0.42, 0.26];
const UNDIMMED = [1, 1, 1];

/** How far from the focus the brightening reaches, as a share of the smaller screen dimension. */
const NEAR_REACH = 0.62;

/** How many distance bands there are around the focus. */
const BANDS = 3;

/** Below this a strand is too faint to be worth a path. */
const HEAD_MIN = 0.006;

/** Shorter than this on screen and a tie reads as a smudge rather than a connection. */
const MIN_SCREEN_PX = 7;

/** The gap a strand leaves at each end: the name's own radius, clamped, plus a little air. */
const GAP_MIN = 1.1;
const GAP_MAX = 46;
const GAP_AIR = 7;

/** The three classes' widths, each opening with the zoom up to its own ceiling. */
function classWidths(zoom: number): [number, number, number] {
  return [
    Math.min(1.3, 0.6 + zoom * 0.32),
    Math.min(1.1, 0.5 + zoom * 0.28),
    Math.min(0.9, 0.4 + zoom * 0.18),
  ];
}

/**
 * Whether the reader's filter lets a name through.
 *
 * A name no scripture we draw claims is shown regardless — `cs.length ? … : true`. That is
 * deliberate and worth keeping: the filter is "show me these scriptures", and a figure belonging to
 * none of them is not a member of any group the reader switched off. Hiding it would make the
 * filter mean "show me ONLY these", which is a different question.
 */
export function nodeVisible(node: WebNode, off: OffSwitches, palette: Readonly<Record<string, string>>): boolean {
  if (off[`k_${node.kind}`] === true) return false;
  const named = [...node.cs].filter(k => palette[k] !== undefined);
  return named.length > 0 ? named.some(k => off[`c_${k}`] !== true) : true;
}

/** How much the web has gathered toward what is open: none far out, all of it close in. */
export function webPool(zoom: number, zooms: WebZooms): number {
  return Math.min(1, Math.max(0, (zoom - zooms.poolFrom) / zooms.poolSpan));
}

/** Which of the three classes a tie belongs to. */
export function strandClass(walkedA: boolean, walkedB: boolean, soft: boolean): number {
  if (soft) return 2;
  if (walkedA && walkedB) return 0;
  return walkedA || walkedB ? 1 : 2;
}

/** What the reader has open, has under the pointer, and has already walked. */
export interface WebFocus {
  /** The index of the figure the reader opened, or null. */
  focus?: number | null;
  /** The index under the pointer, or null. */
  hover?: number | null;
  /** Whether a name has been walked. */
  walked?: (i: number) => boolean;
  /** Whether a tie is a soft one — inferred rather than stated. Keyed `min:max`. */
  weak?: ReadonlySet<string>;
}

/**
 * Draw the resident web. Returns how many strands actually landed.
 *
 * The ties of the OPEN and HOVERED names are skipped entirely here — they are drawn by the selected
 * fan, at full strength, and drawing them twice would make them the only thick lines on the screen
 * for the wrong reason.
 */
export function drawRealWeb(
  ctx: DrawContext,
  view: View,
  nodes: readonly WebNode[],
  edges: readonly WebEdge[],
  world: WebWorld,
  zooms: WebZooms,
  fan: EdgeFan,
  stamp: number,
  focus: WebFocus = {},
): number {
  const z = view.cam.z;
  if (z <= zooms.from) return 0;
  const W = view.view.width;
  const H = view.view.height;
  const sq = world.squash || 1;
  const fid = focus.focus ?? null;
  const hid = focus.hover ?? null;
  const walked = focus.walked ?? ((): boolean => false);
  const weak = focus.weak;

  /* Where the light gathers: the open figure, or the middle of the screen when nothing is open. */
  const open = fid === null ? undefined : nodes[fid];
  const centre = open === undefined ? { x: W / 2, y: H / 2 } : toScreen(view, open.x, open.y);
  const reach = Math.min(W, H) * NEAR_REACH;
  const pool = webPool(z, zooms);
  const ambient = AMBIENT_FLOOR + AMBIENT_RANGE * pool;
  const dim = open === undefined ? UNDIMMED : DIMMED;
  const widths = classWidths(z);
  let drawn = 0;

  for (const [ia, ib] of edges) {
    /* The open and hovered names' own ties belong to the selected fan, not to the ambient web. */
    if (ia === fid || ib === fid || ia === hid || ib === hid) continue;
    const a = nodes[ia];
    const b = nodes[ib];
    if (a === undefined || b === undefined) continue;
    const pa = toScreen(view, a.x, a.y);
    const pb = toScreen(view, b.x, b.y);
    /* Both ends off the same side: cheapest possible rejection, and it takes most of them. */
    if ((pa.x < 0 && pb.x < 0) || (pa.x > W && pb.x > W) || (pa.y < 0 && pb.y < 0) || (pa.y > H && pb.y > H)) continue;
    /* Measured in the WORLD, so a zoom never changes which ties exist. */
    if (Math.hypot(b.x - a.x, (b.y - a.y) / sq) > world.ambientMax) continue;
    if (Math.hypot(pb.x - pa.x, pb.y - pa.y) < MIN_SCREEN_PX) continue;
    const soft = weak?.has(`${Math.min(ia, ib)}:${Math.max(ia, ib)}`) === true;
    if (Math.max(a.w, b.w) < world.minWeight) continue;
    /* CHECKED before the geometry, CHARGED only once a strand is really drawn. */
    if (!fan.take(ia, FAN_CAP, stamp) || !fan.take(ib, FAN_CAP, stamp)) continue;

    const cls = strandClass(walked(ia), walked(ib), soft);
    /* The control point is pulled toward the origin, so the whole web bows around the centre. */
    const control = toScreen(view, ((a.x + b.x) * 0.5) * (1 - BEND), ((a.y + b.y) * 0.5) * (1 - BEND));
    const d = Math.hypot((pa.x + pb.x) * 0.5 - centre.x, (pa.y + pb.y) * 0.5 - centre.y);
    const band = Math.min(BANDS - 1, Math.floor((1 - Math.min(1, d / reach)) * BANDS));
    const head = (HEAD[cls] ?? 0) * ambient * (1 + ((NEAR[band] ?? 1) - 1) * pool) * (dim[cls] ?? 1);
    if (head < HEAD_MIN) continue;
    fan.bump(ia, stamp);
    fan.bump(ib, stamp);
    const gapA = Math.max(GAP_MIN, Math.min(GAP_MAX, a.r * z)) + GAP_AIR;
    const gapB = Math.max(GAP_MIN, Math.min(GAP_MAX, b.r * z)) + GAP_AIR;
    if (strand(ctx, pa.x, pa.y, control.x, control.y, pb.x, pb.y, gapA, gapB, head, widths[cls] ?? 1, cls === 2)) {
      drawn += 1;
    }
  }
  return drawn;
}
